if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('#predictive-search');
      if (!this.input || !this.predictiveSearchResults) return;
      this.requestId = 0;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.input.setAttribute('aria-controls', 'predictive-search-options');
      this.input.removeAttribute('aria-owns');
      this.input.addEventListener('input', () => this.onChange(), options);
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim()) this.onChange();
      }, options);
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      }, options);
      this.querySelector('form')?.addEventListener('submit', () => this.close(), options);
      this.status = document.createElement('div');
      this.status.className = 'visually-hidden';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-atomic', 'true');
      this.append(this.status);
      this.close();
    }

    disconnectedCallback() {
      this.close();
      this.listeners?.abort();
      this.listeners = null;
      this.status?.remove();
    }

    onChange() {
      this.close();
      const searchTerm = this.input.value.trim();
      if (!searchTerm) return;
      this.timer = setTimeout(() => this.getSearchResults(searchTerm), 300);
    }

    async getSearchResults(searchTerm) {
      const requestId = ++this.requestId;
      this.request?.abort();
      this.request = new AbortController();
      try {
        const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[limit_scope]=each&section_id=predictive-search`, { signal: this.request.signal });
        if (!response.ok) throw new Error(String(response.status));
        const text = await response.text();
        if (requestId !== this.requestId || !this.isConnected || this.input.value.trim() !== searchTerm || document.activeElement !== this.input) return;
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Missing predictive search section');
        this.predictiveSearchResults.innerHTML = section.innerHTML;
        this.open();
      } catch (error) {
        if (requestId === this.requestId) this.close();
      }
    }

    getOptions() {
      return Array.from(this.predictiveSearchResults.querySelectorAll('#predictive-search-options [role="option"]')).filter((option) => option.querySelector('a[href]'));
    }

    clearSelection() {
      this.input?.removeAttribute('aria-activedescendant');
      this.predictiveSearchResults?.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
    }

    onKeydown(event) {
      if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key === 'Escape') {
        if (this.timer || this.request || this.input.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
        return;
      }
      if (this.input.getAttribute('aria-expanded') !== 'true') return;
      const options = this.getOptions();
      const selected = options.findIndex((option) => option.id === this.input.getAttribute('aria-activedescendant'));
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!options.length) return;
        event.preventDefault();
        const next = selected === -1 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (selected + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        this.clearSelection();
        options[next].setAttribute('aria-selected', 'true');
        this.input.setAttribute('aria-activedescendant', options[next].id);
        options[next].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && selected !== -1) {
        event.preventDefault();
        const link = options[selected].querySelector('a[href]');
        this.close();
        link.click();
      } else if (event.key === 'Tab') {
        this.clearSelection();
      }
    }

    open() {
      this.clearSelection();
      const listbox = this.predictiveSearchResults.querySelector('#predictive-search-options');
      const hasOptions = this.getOptions().length > 0;
      if (listbox) listbox.hidden = !hasOptions;
      this.predictiveSearchResults.style.display = 'block';
      this.input.setAttribute('aria-expanded', String(hasOptions));
      this.status.textContent = this.predictiveSearchResults.querySelector('.predictive-search-no-results-message')?.textContent.trim() || '';
    }

    close() {
      clearTimeout(this.timer);
      this.timer = null;
      this.request?.abort();
      this.request = null;
      this.requestId = (this.requestId || 0) + 1;
      this.clearSelection();
      if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
      this.input?.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = '';
    }
  }
  customElements.define('predictive-search', PredictiveSearch);
}
